import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ClipboardList, Clock, Wrench, AlertTriangle, CalendarDays, WifiOff, TimerOff, CalendarRange,
} from "lucide-react";
import { api } from "../lib/api";
import { Skeleton } from "./ui/skeleton";
import { PriorityBadge } from "./badges";

const TYPE_LABELS = { preventiva: "Preventiva", correctiva: "Correctiva", instalacion: "Instalación" };

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</p>
        <span className="rounded-md p-1.5" style={{ backgroundColor: `${color}1A`, color }}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <p className="mt-1.5 font-heading text-2xl font-black tracking-tight text-slate-900">{value}</p>
    </div>
  );
}

function Panel({ title, icon: Icon, testId, children }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white" data-testid={testId}>
      <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-3">
        <Icon className="h-4 w-4 text-primary" />
        <h3 className="font-heading text-sm font-bold text-slate-900">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-slate-400">{text}</p>;
}

export function DashboardSummary() {
  const [data, setData] = useState(null);

  useEffect(() => {
    const load = () => api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => {});
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!data) {
    return <Skeleton className="h-48 w-full rounded-lg" data-testid="dashboard-summary-loading" />;
  }

  const { orders, stale_orders, visits_today, visits_week_by_technician, offline_devices } = data;

  return (
    <div className="space-y-4" data-testid="dashboard-summary">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard icon={ClipboardList} label="Órdenes abiertas" value={orders.abiertas} color="#0F62FE" testId="stat-open-orders" />
        <StatCard icon={Clock} label="Pendientes" value={orders.pendiente} color="#F1C21B" testId="stat-pending-orders" />
        <StatCard icon={Wrench} label="En progreso" value={orders.en_progreso} color="#0F62FE" testId="stat-inprogress-orders" />
        <StatCard icon={AlertTriangle} label="Escaladas" value={orders.escalado} color="#FA4D56" testId="stat-escalated-orders" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Visitas programadas de hoy" icon={CalendarDays} testId="panel-visits-today">
          {visits_today.length === 0 ? (
            <Empty text="Sin visitas programadas para hoy." />
          ) : (
            <ul className="space-y-2">
              {visits_today.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`visit-today-${v.id}`}>
                  <span className="min-w-0 truncate">
                    <span className="font-mono font-semibold text-slate-900">{v.scheduled_at.slice(11, 16)}</span>
                    {" · "}{v.client_name}
                    <span className="text-slate-500">{" · "}{v.technician_name}</span>
                  </span>
                  <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 border border-blue-200">
                    {TYPE_LABELS[v.visit_type] || v.visit_type}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Visitas de la semana por técnico" icon={CalendarRange} testId="panel-visits-week">
          {visits_week_by_technician.length === 0 ? (
            <Empty text="Sin visitas esta semana." />
          ) : (
            <ul className="space-y-3">
              {visits_week_by_technician.map((t) => (
                <li key={t.technician} data-testid={`visit-week-${t.technician}`}>
                  <p className="text-sm font-semibold text-slate-900">
                    {t.technician}
                    <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700 border border-blue-200">
                      {t.count} visita{t.count === 1 ? "" : "s"}
                    </span>
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {t.visits.map((v) => (
                      <li key={v.id} className="text-xs text-slate-500">
                        {v.scheduled_at.slice(5, 10).split("-").reverse().join("/")} {v.scheduled_at.slice(11, 16)} · {v.client_name} · {TYPE_LABELS[v.visit_type] || v.visit_type}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Dispositivos fuera de línea" icon={WifiOff} testId="panel-offline-devices">
          {offline_devices.length === 0 ? (
            <Empty text="Todos los dispositivos están en línea." />
          ) : (
            <ul className="space-y-2">
              {offline_devices.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`offline-device-${d.id}`}>
                  <span className="min-w-0 truncate">
                    <span className="font-medium text-slate-900">{d.name}</span>
                    <span className="ml-1.5 font-mono text-xs text-slate-500">{d.target}</span>
                  </span>
                  <span className="shrink-0 rounded-full bg-red-50 px-2 py-0.5 font-mono text-xs font-medium text-red-700 border border-red-200">
                    caído {d.downtime}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Tickets con más de 24h sin resolución" icon={TimerOff} testId="panel-stale-orders">
          {stale_orders.length === 0 ? (
            <Empty text="No hay tickets abiertos con más de 24 horas." />
          ) : (
            <ul className="space-y-2">
              {stale_orders.map((o) => (
                <li key={o.id} className="flex items-center justify-between gap-2 text-sm" data-testid={`stale-order-${o.order_number}`}>
                  <span className="min-w-0 truncate">
                    <Link to={`/orders/${o.id}`} className="font-mono font-semibold text-primary hover:underline">
                      {o.order_number}
                    </Link>
                    <span className="text-slate-600">{" · "}{o.client_name}</span>
                    <span className="text-slate-400">{" · "}{o.technician_name}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1.5">
                    <PriorityBadge priority={o.priority} />
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 font-mono text-xs font-medium text-amber-700 border border-amber-200">
                      {o.elapsed}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}
